import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronRight, LogOut } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { TeamCrest } from "../../components/primitives";
import { BrandMark } from "../../components/brand";
import { navItems, settingsSections, type View } from "../../components/shell/nav-config";
import { SeasonSwitcher } from "../../components/shell/SeasonSwitcher";
import { SupportTriggerButton } from "../support/SupportTriggerButton";
import { teamRoleLabel } from "../../lib/labels";
import type { Team } from "../../types";

function Row({ label, badge = 0, onClick, children }: { label: string; badge?: number; onClick: () => void; children?: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="w-full min-h-[52px] flex items-center gap-3 px-4 text-left border-t first:border-t-0 hover:bg-raised transition-colors">
      {children}
      <span className="flex-1 min-w-0 truncate">{label}</span>
      {badge > 0 && <span className="eb-data rounded-full bg-[hsl(var(--warn))] text-background px-2 text-xs tracking-normal">{badge}</span>}
      <ChevronRight className="h-4 w-4 text-[hsl(var(--faint))]" />
    </button>
  );
}

// Hub del club su telefono e tablet: quello che su desktop sta nella sidebar.
export function ClubView({
  team, teams, role, userEmail, canSeeRequests, canManageMembers, isAppAdmin, requestsBadge, onSelect, onSwitchTeam, onSignOut,
}: {
  team: Team;
  teams: Team[];
  role: string;
  userEmail: string;
  canSeeRequests: boolean;
  canManageMembers: boolean;
  isAppAdmin: boolean;
  requestsBadge: number;
  onSelect: (view: View) => void;
  onSwitchTeam: (teamId: string) => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  const items = navItems.filter((it) => it.section === "club" && (it.id !== "requests" || canSeeRequests));
  const sections = settingsSections.filter((s) => !s.adminOnly || canManageMembers);
  return (
    <div className="max-w-xl mx-auto space-y-6">
      <section className="rounded-[20px] bg-card p-4 flex items-center gap-3">
        <TeamCrest team={team} size={44} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{team.name}</p>
          <SeasonSwitcher variant="compact" side="bottom" onManage={() => onSelect("seasons")} />
        </div>
      </section>
      {teams.length > 1 && (
        <Select value={team.id} onValueChange={onSwitchTeam}>
          <SelectTrigger aria-label={t("topbar.switchTeam")}><SelectValue /></SelectTrigger>
          <SelectContent>
            {teams.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <nav className="rounded-[20px] bg-card overflow-hidden">
        {items.map((item) => (
          <Row key={item.id} label={t(item.labelKey)} badge={item.id === "requests" ? requestsBadge : 0} onClick={() => onSelect(item.id)} />
        ))}
        {isAppAdmin && <Row label={t("nav.admin")} onClick={() => onSelect("admin")} />}
      </nav>
      <section>
        <h2 className="font-semibold mb-2 px-1">{t("nav.settings")}</h2>
        <nav className="rounded-[20px] bg-card overflow-hidden">
          {sections.map((section) => (
            <Row key={section.id} label={t(section.labelKey)} onClick={() => navigate(`/settings#${section.hash}`)} />
          ))}
        </nav>
      </section>
      <section className="space-y-3">
        <p className="text-sm text-muted-foreground px-1 truncate">{userEmail} · {teamRoleLabel(role) || role}</p>
        <SupportTriggerButton activeTeamId={team.id} variant="full" />
        <Button variant="outline" className="w-full" onClick={onSignOut}>
          <LogOut className="h-4 w-4" />
          {t("common:common.logout")}
        </Button>
      </section>
      <div className="pt-2 flex justify-center"><BrandMark size="lg" /></div>
    </div>
  );
}
